import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import { Board } from '@/api';
import { useAuthStore } from '@/stores/auth';

/* Le board dell'utente corrente, per la barra laterale.
 *
 * La barra è sempre a schermo, quindi la lista non può ricaricarsi da sola a
 * ogni navigazione come fanno le griglie: vive qui, si riempie quando la
 * sessione è nota e viene rinfrescata esplicitamente da chi crea, rinomina o
 * elimina una board (SiteSidebar, SiteHeader, BoardGrid). L'endpoint è
 * l'autocomplete: completo, non paginato, e leggero — solo id e nome.
 */
export const useBoardsStore = defineStore('boards', () => {
  const auth = useAuthStore();

  const items = ref([]);

  /* Ultimo import finito, per chi mostra quella board altrove.
   *
   * Serve perché una board si può riempire trascinandoci sopra una cartella
   * *dalla colonna*, cioè da un componente che non è quello che disegna la
   * griglia: senza un segnale, la pagina della board resterebbe com'era e
   * l'import sembrerebbe non aver fatto nulla. Il `giro` cresce a ogni
   * import, così due import di fila sulla stessa board si distinguono. */
  const importata = ref({ id: null, giro: 0 });

  function segnalaImport(id) {
    importata.value = { id, giro: importata.value.giro + 1 };
  }

  /* Ultima board creata, modificata o eliminata, per chi la mostra altrove.
   *
   * Stesso motivo dell'import, un gradino più su: una board si crea dal menu
   * in alto ed è la colonna a sinistra a offrire rinomina, privacy ed
   * eliminazione — tutti posti che non sono la griglia. Senza un segnale la
   * griglia continuava a mostrare la board eliminata (vuota, perché i suoi pin
   * erano spariti davvero) finché non si ricaricava la pagina.
   *
   * La griglia è l'unica ad ascoltare, e la colonna si ricarica per conto suo:
   * questo non è un event bus, è il minimo per non tenere due elenchi della
   * stessa cosa che si contraddicono a schermo. `giro` cresce sempre, così due
   * volte la stessa board si distinguono. */
  const mutata = ref({ tipo: null, board: null, giro: 0 });

  /** @param tipo 'creata' | 'modificata' | 'eliminata' */
  function segnalaMutazione(tipo, board) {
    mutata.value = { tipo, board, giro: mutata.value.giro + 1 };
  }

  async function reload() {
    if (!auth.username) {
      items.value = [];
      return;
    }
    try {
      /* Arriva già alfabetico dall'API, che è la fonte unica dell'ordine per
       * tutti i selettori. Il riordino resta come rete: una lista servita da
       * una cache vecchia non deve far ballare la colonna. */
      const list = await Board.autocomplete(auth.username);
      items.value = [...list].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      );
    } catch {
      // La barra non è la fonte primaria delle board: se il carico fallisce
      // resta quello che c'era, senza uno stato d'errore tutto suo.
    }
  }

  // Login e logout riempiono e svuotano la lista senza che nessun componente
  // debba ricordarsi di farlo.
  watch(() => auth.username, () => reload(), { immediate: true });

  return { items, importata, mutata, reload, segnalaImport, segnalaMutazione };
});
